import axios, { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import type { AxiosRequestConfig } from 'axios';

// ─── Typed Error ────────────────────────────────────────────────────────────

/**
 * Custom ApiError class to normalize all HTTP errors across the application.
 * It includes status, response body, and a descriptive message.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ─── Central axios instance ──────────────────────────────────────────────

/**
 * Base httpClient configured with default baseURL, timeouts, and headers.
 */
export const httpClient = axios.create({
  baseURL: '/api',
  timeout: 15_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Request interceptor ─────────────────────────────────────────────────────

/**
 * Request interceptor for injecting authentication headers or global configuration
 * before the request is sent to the server.
 */
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  error => Promise.reject(error)
);

// ─── Response interceptor ────────────────────────────────────────────────────

/**
 * Response interceptor to handle successful responses and normalize errors.
 * All HTTP failures are transformed into ApiError instances.
 */
httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;
    const message =
      (body as Record<string, string>)?.message ?? error.message ?? `HTTP error ${status}`;

    return Promise.reject(new ApiError(status, body, message));
  }
);

// ─── Fetcher ────────────────────────────────────────────────────

/**
 * Generic fetcher utility to simplify GET requests.
 * Primarily used by React Query's query functions.
 */
export const fetcher = async <T = unknown>(
  args: string | [string, AxiosRequestConfig]
): Promise<T> => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args, {}];

    const res = await httpClient.get<T>(url, config);

    return res.data;
  } catch (error) {
    console.error('Fetcher failed:', error);
    throw error;
  }
};
